// maths.portll.net — the per-child dashboard (plan §1.6).
//
// The grown-up's home for one child: a single "This week" nudge, then the whole
// year's missions grouped by strand with an honest, derived state on each. Every
// bit of state comes from the family's OWN live records (completions + datasets) —
// there is no server-held progress or grade. Language stays warm and off-school:
// progress is "explored", never passed/assessed. Firestore rules re-prove that
// this parent owns this child on every read, so the childId in the URL is only a
// convenience, never the trust boundary.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useProfiles } from "../contexts/ProfileContext.jsx";
import {
  fetchPublishedTasksForYear,
  subscribeCompletions,
  subscribeDatasets,
  yearLabel,
} from "../lib/tasks.js";
import { PageHeader, Card, Spinner, Note, EmptyState, injectStyles } from "../components/ui.jsx";
import ThisWeekCard from "../components/ThisWeekCard.jsx";
import GameProgressCard from "../components/GameProgressCard.jsx";
import TaskListByStrand, {
  readPlannedSet,
  persistPlannedSet,
} from "../components/TaskListByStrand.jsx";

export default function Dashboard() {
  const { childId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { profiles, loading: profilesLoading, displayNameFor } = useProfiles();

  const child = useMemo(() => profiles.find((p) => p.id === childId) || null, [profiles, childId]);
  const year = child ? child.yearLevel : null;

  const [tasks, setTasks] = useState(null); // null = still loading
  const [tasksError, setTasksError] = useState(false);
  const [completions, setCompletions] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [planned, setPlanned] = useState(() => new Set());

  // Not our child (after profiles have loaded) → back to the picker.
  useEffect(() => {
    if (!profilesLoading && !child) nav("/grown-ups/children", { replace: true });
  }, [profilesLoading, child, nav]);

  // Device-local "Planned" markers for this child.
  useEffect(() => {
    setPlanned(childId ? readPlannedSet(childId) : new Set());
  }, [childId]);

  // Published tasks for the child's year (one-shot; re-run if the year changes).
  useEffect(() => {
    if (year == null) return;
    let alive = true;
    setTasks(null);
    setTasksError(false);
    fetchPublishedTasksForYear(year)
      .then((rows) => {
        if (alive) setTasks(rows);
      })
      .catch(() => {
        if (alive) {
          setTasks([]);
          setTasksError(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [year]);

  // Live family records — cleaned up on unmount / child switch.
  useEffect(() => {
    if (!user || !childId) return undefined;
    const offCompletions = subscribeCompletions(user.uid, childId, setCompletions);
    const offDatasets = subscribeDatasets(user.uid, childId, setDatasets);
    return () => {
      offCompletions();
      offDatasets();
    };
  }, [user, childId]);

  function markPlanned(taskId) {
    setPlanned((prev) => {
      if (prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.add(taskId);
      persistPlannedSet(childId, next);
      return next;
    });
  }

  // Explored-so-far, framed warmly (never "passed").
  const { exploredCount, startableCount } = useMemo(() => {
    const rows = tasks || [];
    const doneIds = new Set(completions.map((c) => c && c.taskId).filter(Boolean));
    const startable = rows.filter((t) => !t.comingSoon);
    return {
      exploredCount: startable.filter((t) => doneIds.has(t.id)).length,
      startableCount: startable.length,
    };
  }, [tasks, completions]);

  // ── loading / guard states ────────────────────────────────────────────────
  if (profilesLoading || !child) {
    return (
      <main className="container dash-center">
        <Spinner size={28} />
      </main>
    );
  }

  const heading = displayNameFor(child) || "This child";

  return (
    <main className="container dash">
      <PageHeader title={heading} subtitle={`Year ${yearLabel(child.yearLevel)}`} back="/grown-ups/children" />

      <GameProgressCard uid={user?.uid} childId={childId} name={displayNameFor(child)} />

      {startableCount > 0 && (
        <Note tone="brand" className="dash-progress">
          {exploredCount === 0
            ? "Nothing explored yet — pick something small to start."
            : `You've explored ${exploredCount} of ${startableCount} missions together so far.`}
        </Note>
      )}

      {tasks === null ? (
        <div className="dash-center dash-center--tasks">
          <Spinner size={26} />
        </div>
      ) : tasksError ? (
        <Card>
          <Note tone="danger">
            We couldn't load this year's missions just now. Check your connection and try again in a moment.
          </Note>
        </Card>
      ) : tasks.length === 0 ? (
        <EmptyState
          icon="🌱"
          title="No missions here yet"
          hint="Missions for this year are still being planted. They'll appear here as soon as they're ready."
        />
      ) : (
        <>
          <ThisWeekCard
            childId={childId}
            tasks={tasks}
            completions={completions}
            onPlan={markPlanned}
          />
          <div className="dash-list">
            <TaskListByStrand
              childId={childId}
              tasks={tasks}
              completions={completions}
              datasets={datasets}
              planned={planned}
            />
          </div>
        </>
      )}
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────────────
const DASH_CSS = `
.dash{display:block;}
.dash-progress{margin:0 0 1.25rem;}
.dash-list{margin-top:1.5rem;}
.dash-center{display:grid;place-items:center;min-height:50vh;}
.dash-center--tasks{min-height:32vh;}
`;

injectStyles("maths-dashboard-styles", DASH_CSS);
